import {
  UpdateMultipleDirectorInputType,
  updateMultipleDirectors,
  useCompanyData,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import useStakeholdings from '@commenda/api/cap-table/useStakeholdings';
import { PlusIcon, ProceedIcon } from '@commenda/icons';
import {
  Button,
  Icons,
  Loading,
  LoadingIndicator,
  MultiSelect,
  useToggle,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  DirectorPersonalDetails,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames, getCorporationName } from '@commenda/utils';
import {
  CorporationType,
  Country,
  LegacyDocumentType,
  StakeholderType,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { getDirectorName } from '../Director/common';
import DirectorModal from '../Director/new/DirectorModal';
import { DirectorCardKS, IncorporationFormHeading } from '../common';
import CorporateStakeholderModal from '../common/corporateStakeholder/CorporateStakeholderModal';
import StakeholderOptionsModal from '../common/corporateStakeholder/StakeholderOptionsModal';
import DirectorForm from './USDirectorForm';
import { validateDirectorInfo } from './validators';

const DirectorInfo = ({
  next,
  previous,
  data,
  mutate,
  companyId,
  corporationId,
  token,
  setNotification,
  resetFurtherSteps,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  companyId: number;
  corporationId: number;
  token: string;
  setNotification: TSetNotification;
  resetFurtherSteps: () => void;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  // All the directors in the company
  const [directors, setDirectors] = useState<DirectorBase[]>([]);
  const [isSubmitting, setSubmitting] = useState(false);

  const {
    data: companyData,
    error,
    mutate: companyMutate,
  } = useGetAllDirectorsInCompany(data?.companyId);

  const [isDirectorModalShown, setDirectorModalShown] = useState(false);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);
  const selectedDirectorIdsRef = useRef<number[]>([]);

  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const stakeholderTypeModalVisibility = useToggle(false);
  const corporateStakeholderModalVisibility = useToggle(false);
  const [corporateStakeholderToEdit, setCorporateStakeholderToEdit] = useState<
    number | undefined
  >(undefined);

  const {
    corporations,
    mutate: mutateCorporations,
    error: corporationsError,
  } = useCompanyData(companyId);

  const {
    data: stakeholdings,
    mutate: mutateStakeholdings,
    error: stakeholdingsError,
  } = useStakeholdings(corporationId, { capitalContribution: true });

  const corporateMembers = useMemo(() => {
    if (!stakeholdings || !corporations) return [];
    return stakeholdings
      .filter(
        (sh) =>
          sh.stakeholder?.type === StakeholderType.INSTITUTION &&
          sh.stakeholder?.corporation
      )
      .map((sh) => {
        const corpId = sh.stakeholder?.corporation?.id;
        if (!corpId) return null;
        return corporations.find((corp) => corp.id === corpId);
      })
      .filter(Boolean);
  }, [stakeholdings, corporations]);

  useEffect(() => {
    setDirectors(
      companyData?.directors?.map((eachDirector) => {
        const passportProofFile = eachDirector.directorFiles?.find(
          (file) => file.documentType === LegacyDocumentType.Passport
        );
        return {
          id: eachDirector?.id,
          directorFirstName: eachDirector.directorFirstName,
          directorMiddleName: eachDirector.directorMiddleName,
          directorLastName: eachDirector.directorLastName,
          email: eachDirector.email,
          operatingAddress:
            eachDirector.operatingAddress || defaultAddressSchema,
          personalDetails: {
            nationality: (
              eachDirector.personalDetails as DirectorPersonalDetails
            )?.nationality as Country,
            residence: (eachDirector.personalDetails as DirectorPersonalDetails)
              ?.residence as Country,
          },
          number: eachDirector.number,
          corporationHasDirector: eachDirector.corporationHasDirector,
          dateOfBirth: eachDirector?.dateOfBirth,
          governmentId: eachDirector?.governmentId,
          directorFiles: eachDirector?.directorFiles,
          passportProof: {
            id: passportProofFile?.id,
            documentType: LegacyDocumentType.Passport,
            fileName: passportProofFile?.fileName,
            fileUrl: passportProofFile?.fileUrl,
          },
        };
      }) || []
    );
  }, [companyData]);

  useEffect(() => {
    const alreadySelectedDirectors = [...selectedDirectorIdsRef.current];

    companyData?.directors
      ?.filter((dir) =>
        dir.corporationHasDirector?.find(
          (chd) =>
            chd.corporationId === corporationId &&
            chd.directorId === dir.id &&
            chd.additionalDirectorInformation?.isDirector
        )
      )
      .forEach((director) => {
        if (director?.WorkRequests?.find((wrkReq) => wrkReq.id === id)) {
          if (!alreadySelectedDirectors.includes(director.id))
            alreadySelectedDirectors.push(director.id);
        }
      });

    setSelectedDirectorIds(alreadySelectedDirectors);
  }, [companyData]);

  if (!companyData) {
    if (error) {
      console.error(error);
    }
    return <Loading />;
  }

  if (corporationsError) {
    console.error(corporationsError);
  }

  if (stakeholdingsError) {
    console.error(stakeholdingsError);
  }

  const onSubmit = async () => {
    setSubmitting(true);

    if (!data?.id) {
      setNotification({
        show: true,
        valid: false,
        message: 'Work request ID is missing. Please refresh the page.',
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    if (selectedDirectorIds.length === 0) {
      setNotification({
        show: true,
        valid: false,
        message: `US Entity requires you to have at least one director.`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const formError = validateDirectorInfo(directors, selectedDirectorIds);

    if (formError.length) {
      setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));

      setNotification({
        show: true,
        valid: false,
        message: `Please fill in the missing information for directors by clicking on "Edit".`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const directorsToDisconnect = companyData?.directors
      ?.filter((eachDirector) =>
        eachDirector?.corporationHasDirector?.find(
          (dirWithCorp) =>
            dirWithCorp.corporationId === data.corporationId &&
            dirWithCorp.directorId === eachDirector.id &&
            dirWithCorp.additionalDirectorInformation?.isDirector &&
            !selectedDirectorIds.includes(dirWithCorp.directorId)
        )
      )
      ?.map((each) => each.id);

    const directorUpdates: UpdateMultipleDirectorInputType[] = [
      ...selectedDirectorIds.map((eachDirId) => ({
        id: eachDirId,
        wrkReqId: data.id,
        corporationHasDirector: [
          {
            corporationId: data.corporationId,
            directorId: eachDirId,
            additionalDirectorInformation: {
              isDirector: true,
            },
          },
        ],
      })),
      ...directorsToDisconnect.map((eachDirId) => ({
        id: eachDirId,
        workRequestIdToDisconnect: data.id,
        corporationIdsToDisconnect: [data.corporationId],
      })),
    ];

    const response = await updateMultipleDirectors(directorUpdates, token);

    if (!response) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      setSubmitting(false);
      return;
    }

    await mutate();
    next();
    setSubmitting(false);
  };

  const hasDirectors = companyData?.directors?.length;

  return (
    <div className="flex flex-col gap-6 max-w-[700px]">
      <IncorporationFormHeading
        text={
          data?.requestData?.corporationType === CorporationType.CCorp
            ? 'Add Director(s)'
            : 'Add Member(s)'
        }
        additionalText="Please provide information about your company’s directors. At least one director is required."
      />

      {hasDirectors ? (
        <MultiSelect
          id="add-director-multiselect"
          options={(companyData?.directors || []).map((dir) => ({
            label: getDirectorName(dir),
            value: dir.id.toString(),
          }))}
          onValueChange={(val: string[]) => {
            setSelectedDirectorIds(val.map(Number));
            selectedDirectorIdsRef.current = val.map(Number);
            resetFurtherSteps();
          }}
          defaultValue={selectedDirectorIds.map(String)}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            onAddItem: () => {
              if (data?.requestData?.corporationType === CorporationType.LLC) {
                stakeholderTypeModalVisibility.on();
              } else {
                setDirectorModalShown(true);
              }
            },
            placeholder: `${
              data?.requestData?.corporationType === CorporationType.CCorp
                ? 'Add new director'
                : 'Add new member'
            }`,
          }}
          placeholder={
            data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Add or select directors'
              : 'Add or select members'
          }
        />
      ) : (
        <Button
          variant="secondary"
          className="w-full flex gap-2 items-center justify-center"
          onClick={() => {
            if (data?.requestData?.corporationType === CorporationType.LLC) {
              stakeholderTypeModalVisibility.on();
            } else {
              setDirectorModalShown(true);
            }
          }}
        >
          <PlusIcon className="text-neutral-1" />
          <span>
            {data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Add director'
              : 'Add member'}
          </span>
        </Button>
      )}

      <div>
        {directors
          ?.filter((dir) => selectedDirectorIds.includes(dir.id))
          .map((director) => (
            <DirectorCardKS
              directorForm={DirectorForm}
              key={director.id}
              director={director}
              companyDataMutate={companyMutate}
              setNotification={setNotification}
              corporationId={corporationId}
              companyId={companyId}
              token={token}
              hasError={directorIdsWithError.has(director.id)}
              onErrorResolve={() =>
                setDirectorIdsWithError((prevIds) => {
                  const newIds = new Set(prevIds);
                  newIds.delete(director.id);
                  return newIds;
                })
              }
              onRemoveDirector={async (directorId: number) => {
                setSelectedDirectorIds((prev) =>
                  prev.filter((dirId) => dirId !== director.id)
                );
                selectedDirectorIdsRef.current = selectedDirectorIds;
                resetFurtherSteps();
              }}
              modalConfigOverride={{
                heading:
                  data?.requestData?.corporationType === CorporationType.LLC
                    ? 'Edit member information'
                    : 'Edit director information',
                subheading:
                  data?.requestData?.corporationType === CorporationType.LLC
                    ? 'Edit details below to update member'
                    : 'Edit details below to update director',
                dialogTitle:
                  data?.requestData?.corporationType === CorporationType.LLC
                    ? 'Edit member'
                    : 'Edit director',
              }}
              workRequestData={data}
              allCorporations={corporations}
            />
          ))}
        {data?.requestData?.corporationType === CorporationType.LLC &&
          corporateMembers.map((corp) => {
            if (!corp) return null;
            const corporationName = getCorporationName(corp);
            return (
              <div
                key={corp.id}
                className="p-1.5 border-b border-primary flex justify-between items-center"
              >
                <div className="flex gap-2 items-center">
                  <Icons.Building01 className="text-fg-quinary h-5 w-5" />
                  <h4 className="text-text-primary txt-small-medium">
                    {corporationName}
                  </h4>
                </div>
                <div className="flex gap-1 items-center">
                  <div className="p-2 hover:bg-bg-secondary-hover rounded-sm">
                    <Icons.Pencil02
                      onClick={() => {
                        setCorporateStakeholderToEdit(corp.id);
                        corporateStakeholderModalVisibility.on();
                      }}
                      className="h-5 w-5 text-button-tertiary-fg hover:text-button-tertiary-fg-hover cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className="max-w-[700px]">
        <DirectorModal
          isOpen={isDirectorModalShown}
          setOpen={setDirectorModalShown}
          companyDataMutate={companyMutate}
          DirectorForm={DirectorForm}
          setNotification={setNotification}
          companyId={companyId}
          corporationId={corporationId}
          token={token}
          selectedDirectorIds={selectedDirectorIds}
          setSelectedDirectorIds={setSelectedDirectorIds}
          modalConfig={{
            isDirector: true,
            heading:
              data?.requestData?.corporationType === CorporationType.CCorp
                ? 'Add Director'
                : 'Add Member',
            dialogTitle:
              data?.requestData?.corporationType === CorporationType.CCorp
                ? 'Add Director'
                : 'Add Member',
            postSave: (dir) => {
              if (!selectedDirectorIdsRef.current.includes(dir.id)) {
                selectedDirectorIdsRef.current.push(dir.id);
              }
            },
          }}
          workRequestData={data}
          allCorporations={corporations}
        />
        {data?.requestData?.corporationType === CorporationType.LLC && (
          <>
            <StakeholderOptionsModal
              isShown={stakeholderTypeModalVisibility.value}
              toggle={stakeholderTypeModalVisibility.toggle}
              onShowDirectorModal={() => {
                stakeholderTypeModalVisibility.off();
                setDirectorModalShown(true);
              }}
              onShowCorporateShareholderModal={() => {
                stakeholderTypeModalVisibility.off();
                corporateStakeholderModalVisibility.on();
              }}
              isMember={true}
            />
            <CorporateStakeholderModal
              selectedCorporateStakeholderId={corporateStakeholderToEdit}
              setSelectedCorporateStakeholderId={setCorporateStakeholderToEdit}
              companyId={companyId}
              isShown={corporateStakeholderModalVisibility.value}
              toggle={corporateStakeholderModalVisibility.toggle}
              postSave={async ({
                corporationId: corporateStakeholderId,
                corporationName,
              }) => {
                await mutateCorporations();
                await companyMutate();
                await mutateStakeholdings();
                setNotification({
                  show: true,
                  valid: true,
                  title: 'Success',
                  message: 'Corporate member added successfully',
                });
              }}
              mutate={async () => {
                await mutateCorporations();
                await companyMutate();
                await mutateStakeholdings();
              }}
              setNotification={setNotification}
              headerTextOverride={
                corporateStakeholderToEdit
                  ? 'Edit corporate member'
                  : 'Add corporate member'
              }
              DirectorForm={DirectorForm}
              directors={companyData?.directors || []}
              mutateDirectors={companyMutate}
              corporations={corporations || []}
              workRequestData={data}
            />
          </>
        )}
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || directors?.length === 0}
        >
          {isSubmitting ? <LoadingIndicator size={32} /> : null}
          <span className={classNames(isSubmitting ? 'ml-2' : '')}>
            Proceed
          </span>
          {!isSubmitting && <ProceedIcon className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default DirectorInfo;
